/**
 * Main Menu - Start screen with faction and difficulty selection
 * Design inspired by classic strategy game faction selection screens
 */

import { FACTIONS } from '@/game/factions/FactionData';
import {
  type DifficultyLevel,
  getDifficultyName,
  getDifficultyDescription,
} from '@/game/ai/DifficultySettings';
import type { FactionId } from '@/types';
import { COMMUNITY_LINKS } from '@/ui/CommunityLinks';

// Faction descriptions for selection
const FACTION_DESCRIPTIONS: Record<FactionId, string> = {
  yan: '尚武好战，以战养战。',
  han: '据险而守，以逸待劳。',
  lin: '游击战术，神出鬼没。',
  shang: '以商立国，金钱万能。',
  xuan: '诡道谋略，不战而屈人之兵。',
  tie: '重甲突击，碾压一切。',
  yun: '来去如风，四海为家。',
  neutral: '中立势力',
};

// Faction bonuses
const FACTION_BONUSES: Record<FactionId, string> = {
  yan: '+15%',
  han: '+20%',
  lin: '+30%',
  shang: '+40%',
  xuan: '+30%',
  tie: '+30%',
  yun: '+35%',
  neutral: '',
};

// Faction bonus labels
const FACTION_BONUS_LABELS: Record<FactionId, string> = {
  yan: '攻击伤害',
  han: '防御加成',
  lin: '弓兵攻击',
  shang: '资源产出',
  xuan: '外交成功',
  tie: '枪兵血量',
  yun: '移动速度',
  neutral: '',
};

// Chinese subtitles for factions
const FACTION_SUBTITLES: Record<FactionId, string> = {
  yan: '炎阳帝国',
  han: '北境霜庭',
  lin: '翠林联盟',
  shang: '黄金商会',
  xuan: '幽冥玄门',
  tie: '钢铁要塞',
  yun: '苍穹游牧',
  neutral: '中立势力',
};

// Faction card border colors
const FACTION_BORDER_COLORS: Record<FactionId, string> = {
  yan: '#c73e3a',
  han: '#5b9bd5',
  lin: '#4caf50',
  shang: '#ffd700',
  xuan: '#7b1fa2',
  tie: '#607d8b',
  yun: '#03a9f4',
  neutral: '#666',
};

export interface MainMenuCallbacks {
  onStartGame: (factionId: FactionId, difficulty: DifficultyLevel) => void;
}

export class MainMenu {
  private container: HTMLElement;
  private selectedFaction: FactionId = 'lin';
  private selectedDifficulty: DifficultyLevel = 'normal';
  private callbacks: MainMenuCallbacks | null = null;

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'main-menu';
    this.container.innerHTML = `
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+SC:wght@400;700;900&display=swap');

        #main-menu {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 300;
          font-family: 'Noto Serif SC', -apple-system, BlinkMacSystemFont, 'Segoe UI', serif;
          color: #e8e0d0;
          background: #1a1510;
          display: flex;
          flex-direction: column;
          align-items: center;
          overflow-y: auto;
          padding: clamp(8px, 2vh, 30px) 20px;
        }
        #main-menu::before {
          content: '';
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('/images/bg.webp') center/cover no-repeat;
          opacity: 0.6;
          z-index: -1;
        }
        #main-menu::after {
          content: '';
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(ellipse at center top, transparent 0%, rgba(10, 8, 5, 0.7) 70%),
                      linear-gradient(180deg, transparent 60%, rgba(10, 8, 5, 0.9) 100%);
          z-index: -1;
        }
        /* 漂浮粒子效果 */
        .particles {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          z-index: 0;
          overflow: hidden;
        }
        .particle {
          position: absolute;
          width: 3px;
          height: 3px;
          background: radial-gradient(circle, rgba(255, 200, 100, 0.8) 0%, transparent 70%);
          border-radius: 50%;
          animation: float 15s infinite ease-in-out;
        }
        .particle:nth-child(1) { left: 10%; animation-delay: 0s; animation-duration: 18s; }
        .particle:nth-child(2) { left: 20%; animation-delay: 2s; animation-duration: 15s; }
        .particle:nth-child(3) { left: 30%; animation-delay: 4s; animation-duration: 20s; }
        .particle:nth-child(4) { left: 40%; animation-delay: 1s; animation-duration: 16s; }
        .particle:nth-child(5) { left: 50%; animation-delay: 3s; animation-duration: 19s; }
        .particle:nth-child(6) { left: 60%; animation-delay: 5s; animation-duration: 14s; }
        .particle:nth-child(7) { left: 70%; animation-delay: 2.5s; animation-duration: 17s; }
        .particle:nth-child(8) { left: 80%; animation-delay: 4.5s; animation-duration: 21s; }
        .particle:nth-child(9) { left: 90%; animation-delay: 1.5s; animation-duration: 16s; }
        .particle:nth-child(10) { left: 15%; animation-delay: 3.5s; animation-duration: 18s; }
        .particle:nth-child(11) { left: 35%; animation-delay: 0.5s; animation-duration: 15s; }
        .particle:nth-child(12) { left: 55%; animation-delay: 2.2s; animation-duration: 20s; }
        .particle:nth-child(13) { left: 75%; animation-delay: 4.2s; animation-duration: 17s; }
        .particle:nth-child(14) { left: 85%; animation-delay: 1.2s; animation-duration: 19s; }
        .particle:nth-child(15) { left: 25%; animation-delay: 3.2s; animation-duration: 16s; }
        @keyframes float {
          0% {
            transform: translateY(100vh) scale(0);
            opacity: 0;
          }
          10% {
            opacity: 1;
            transform: translateY(90vh) scale(1);
          }
          90% {
            opacity: 0.6;
            transform: translateY(10vh) scale(0.8);
          }
          100% {
            transform: translateY(-5vh) scale(0);
            opacity: 0;
          }
        }
        #main-menu.hidden {
          display: none;
        }

        /* Title */
        .menu-title {
          font-size: clamp(32px, 5vh, 56px);
          font-weight: 900;
          color: #ffd35a;
          text-shadow: 0 0 40px rgba(255, 180, 50, 0.5),
                       0 4px 8px rgba(0, 0, 0, 0.8);
          margin-bottom: 4px;
          letter-spacing: 8px;
        }
        .menu-subtitle {
          font-size: clamp(14px, 2vh, 18px);
          color: #c9b896;
          opacity: 0.9;
          margin-bottom: clamp(12px, 3vh, 40px);
          letter-spacing: 4px;
        }

        /* Faction Grid */
        .menu-section {
          width: 100%;
          max-width: 1100px;
          margin-bottom: clamp(8px, 1.5vh, 24px);
        }
        .faction-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: clamp(6px, 1vh, 12px);
        }
        @media (max-width: 900px) {
          .faction-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        @media (max-width: 500px) {
          .faction-grid {
            grid-template-columns: 1fr;
          }
        }

        /* Faction Card - 古典双层边框风格 */
        .faction-card {
          position: relative;
          background: linear-gradient(135deg, rgba(25, 20, 15, 0.85) 0%, rgba(15, 12, 8, 0.9) 100%);
          border: 1px solid rgba(180, 150, 100, 0.6);
          border-radius: 4px;
          padding: clamp(8px, 1.2vh, 14px) 14px;
          cursor: pointer;
          transition: all 0.3s ease;
          overflow: hidden;
          /* 双层边框效果 */
          box-shadow: inset 0 0 0 1px rgba(120, 100, 60, 0.4),
                      inset 0 0 0 3px rgba(20, 15, 10, 0.8),
                      inset 0 0 0 4px rgba(180, 150, 100, 0.3);
        }
        .faction-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: var(--faction-bg) right center/cover no-repeat;
          opacity: 0.7;
          transition: opacity 0.3s ease;
          z-index: 0;
        }
        .faction-card::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(90deg, rgba(15, 12, 8, 0.9) 0%, rgba(15, 12, 8, 0.5) 40%, transparent 100%);
          z-index: 1;
        }
        .faction-card:hover {
          transform: translateY(-3px);
          border-color: rgba(255, 200, 100, 0.7);
          box-shadow: inset 0 0 0 1px rgba(200, 170, 100, 0.5),
                      inset 0 0 0 3px rgba(20, 15, 10, 0.8),
                      inset 0 0 0 4px rgba(200, 170, 100, 0.4),
                      0 8px 20px rgba(0, 0, 0, 0.4);
        }
        .faction-card:hover::before {
          opacity: 0.85;
        }
        .faction-card.selected {
          border-color: var(--faction-border);
          box-shadow:
            /* 双层边框 */
            inset 0 0 0 1px rgba(var(--faction-glow), 0.6),
            inset 0 0 0 3px rgba(20, 15, 10, 0.9),
            inset 0 0 0 4px rgba(var(--faction-glow), 0.4),
            /* 外发光 */
            0 0 15px rgba(var(--faction-glow), 0.5),
            0 0 30px rgba(var(--faction-glow), 0.3),
            0 0 50px rgba(var(--faction-glow), 0.2);
          animation: selectedPulse 2s ease-in-out infinite;
        }
        .faction-card.selected::before {
          opacity: 0.9;
        }
        .faction-card.selected::after {
          background: linear-gradient(90deg,
            rgba(var(--faction-glow), 0.1) 0%,
            rgba(15, 12, 8, 0.6) 30%,
            transparent 100%);
        }
        @keyframes selectedPulse {
          0%, 100% {
            box-shadow:
              inset 0 0 0 1px rgba(var(--faction-glow), 0.6),
              inset 0 0 0 3px rgba(20, 15, 10, 0.9),
              inset 0 0 0 4px rgba(var(--faction-glow), 0.4),
              0 0 15px rgba(var(--faction-glow), 0.5),
              0 0 30px rgba(var(--faction-glow), 0.3),
              0 0 50px rgba(var(--faction-glow), 0.2);
          }
          50% {
            box-shadow:
              inset 0 0 0 1px rgba(var(--faction-glow), 0.8),
              inset 0 0 0 3px rgba(20, 15, 10, 0.9),
              inset 0 0 0 4px rgba(var(--faction-glow), 0.6),
              0 0 20px rgba(var(--faction-glow), 0.6),
              0 0 40px rgba(var(--faction-glow), 0.4),
              0 0 60px rgba(var(--faction-glow), 0.25);
          }
        }

        /* Card Content */
        .faction-content {
          position: relative;
          z-index: 2;
        }
        .faction-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: clamp(2px, 0.5vh, 8px);
        }
        .faction-icon {
          width: clamp(30px, 4vh, 40px);
          height: clamp(30px, 4vh, 40px);
          border-radius: 50%;
          background: var(--faction-emblem) center/cover no-repeat;
          flex-shrink: 0;
          box-shadow: 0 0 8px rgba(var(--faction-glow), 0.6),
                      0 0 15px rgba(var(--faction-glow), 0.3),
                      inset 0 0 10px rgba(0, 0, 0, 0.3);
          border: 2px solid var(--faction-border);
          position: relative;
        }
        .faction-icon::after {
          content: '';
          position: absolute;
          top: 2px;
          left: 2px;
          right: 2px;
          height: 40%;
          background: linear-gradient(180deg, rgba(255,255,255,0.2) 0%, transparent 100%);
          border-radius: 50% 50% 40% 40%;
          pointer-events: none;
        }
        .faction-name {
          font-weight: 700;
          font-size: clamp(16px, 2.2vh, 20px);
          color: #fff;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
        }
        .faction-subtitle {
          font-size: clamp(10px, 1.3vh, 12px);
          color: #a99878;
          margin-bottom: clamp(4px, 0.8vh, 10px);
        }
        .faction-desc {
          font-size: clamp(11px, 1.4vh, 13px);
          color: #c9b896;
          line-height: 1.4;
          margin-bottom: clamp(4px, 0.8vh, 10px);
        }
        .faction-bonus {
          font-size: clamp(14px, 2vh, 18px);
          font-weight: 700;
          color: #4caf50;
          text-shadow: 0 0 10px rgba(76, 175, 80, 0.5);
        }
        .faction-bonus-label {
          font-size: 11px;
          color: #888;
          margin-left: 4px;
        }

        /* Difficulty Section */
        .section-divider {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 20px;
          margin: clamp(6px, 1.2vh, 24px) 0;
          width: 100%;
          max-width: 1100px;
        }
        .divider-line {
          flex: 1;
          height: 2px;
          background: linear-gradient(90deg,
            transparent 0%,
            rgba(120, 100, 60, 0.3) 20%,
            rgba(180, 150, 100, 0.6) 50%,
            rgba(120, 100, 60, 0.3) 80%,
            transparent 100%);
          position: relative;
        }
        .divider-line::after {
          content: '';
          position: absolute;
          top: -1px;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg,
            transparent 0%,
            rgba(255, 220, 150, 0.2) 50%,
            transparent 100%);
        }
        .divider-text {
          font-size: clamp(12px, 1.6vh, 15px);
          color: #c9b896;
          letter-spacing: 4px;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
        }

        .difficulty-row {
          display: flex;
          gap: clamp(8px, 1vh, 16px);
          flex-wrap: wrap;
          justify-content: center;
        }
        .difficulty-btn {
          position: relative;
          width: 240px;
          padding: clamp(8px, 1.2vh, 16px);
          background: linear-gradient(135deg, rgba(25, 20, 15, 0.85) 0%, rgba(15, 12, 8, 0.9) 100%);
          border: 1px solid rgba(180, 150, 100, 0.6);
          border-radius: 4px;
          color: #e8e0d0;
          cursor: pointer;
          text-align: left;
          transition: all 0.2s ease;
          font-family: inherit;
          overflow: hidden;
          display: flex;
          align-items: center;
          gap: 14px;
          /* 双层边框 */
          box-shadow: inset 0 0 0 1px rgba(120, 100, 60, 0.4),
                      inset 0 0 0 3px rgba(20, 15, 10, 0.8),
                      inset 0 0 0 4px rgba(180, 150, 100, 0.3);
        }
        .difficulty-btn:hover {
          border-color: rgba(255, 200, 100, 0.7);
          transform: translateY(-2px);
          box-shadow: inset 0 0 0 1px rgba(200, 170, 100, 0.5),
                      inset 0 0 0 3px rgba(20, 15, 10, 0.8),
                      inset 0 0 0 4px rgba(200, 170, 100, 0.4),
                      0 8px 20px rgba(0, 0, 0, 0.4);
        }
        .difficulty-btn.selected {
          border-color: #ffd35a;
          background: linear-gradient(135deg, rgba(40, 32, 18, 0.9) 0%, rgba(25, 20, 12, 0.95) 100%);
          box-shadow: inset 0 0 0 1px rgba(255, 200, 100, 0.6),
                      inset 0 0 0 3px rgba(20, 15, 10, 0.9),
                      inset 0 0 0 4px rgba(255, 200, 100, 0.4),
                      0 0 15px rgba(255, 180, 50, 0.3),
                      0 0 30px rgba(255, 180, 50, 0.15);
        }
        .difficulty-icon {
          width: clamp(36px, 5vh, 56px);
          height: clamp(36px, 5vh, 56px);
          flex-shrink: 0;
          background-size: contain;
          background-position: center;
          background-repeat: no-repeat;
        }
        .difficulty-info {
          flex: 1;
        }
        .difficulty-name {
          font-weight: 700;
          font-size: clamp(14px, 2vh, 18px);
          margin-bottom: clamp(2px, 0.5vh, 6px);
          color: #fff;
        }
        .difficulty-desc {
          font-size: clamp(10px, 1.3vh, 12px);
          color: #a99878;
          line-height: 1.4;
        }

        /* Start Button */
        .start-section {
          margin-top: clamp(10px, 2vh, 30px);
          text-align: center;
        }
        .start-btn {
          position: relative;
          padding: clamp(10px, 1.5vh, 18px) clamp(50px, 8vw, 100px);
          font-size: clamp(18px, 2.5vh, 24px);
          font-weight: 700;
          font-family: inherit;
          border: 2px solid #8b6914;
          border-radius: 6px;
          cursor: pointer;
          background: linear-gradient(180deg, #ffe17a 0%, #ffd35a 30%, #c9a227 70%, #a68520 100%);
          color: #2a1a08;
          transition: all 0.2s ease;
          box-shadow: 0 4px 20px rgba(255, 180, 50, 0.5),
                      0 0 40px rgba(255, 180, 50, 0.2),
                      inset 0 2px 0 rgba(255, 255, 255, 0.4),
                      inset 0 -2px 4px rgba(0, 0, 0, 0.2);
          letter-spacing: 6px;
          text-shadow: 0 1px 0 rgba(255, 255, 255, 0.3);
        }
        .start-btn::before {
          content: '';
          position: absolute;
          top: 3px;
          left: 3px;
          right: 3px;
          height: 50%;
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, transparent 100%);
          border-radius: 4px 4px 50% 50%;
          pointer-events: none;
        }
        .start-btn:hover {
          transform: translateY(-3px);
          background: linear-gradient(180deg, #fff18a 0%, #ffe35a 30%, #d4b030 70%, #b89525 100%);
          box-shadow: 0 8px 30px rgba(255, 180, 50, 0.6),
                      0 0 60px rgba(255, 180, 50, 0.3),
                      inset 0 2px 0 rgba(255, 255, 255, 0.5),
                      inset 0 -2px 4px rgba(0, 0, 0, 0.2);
        }
        .start-btn:active {
          transform: translateY(0);
          box-shadow: 0 2px 10px rgba(255, 180, 50, 0.4),
                      inset 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        /* Footer */
        .menu-footer {
          margin-top: clamp(8px, 1.5vh, 24px);
          font-size: 12px;
          color: #666;
        }
        .menu-footer kbd {
          background: rgba(255, 255, 255, 0.1);
          padding: 2px 6px;
          border-radius: 4px;
          font-family: monospace;
        }

        /* Community Links */
        .menu-community {
          display: flex;
          gap: 16px;
          justify-content: center;
          align-items: center;
          margin-top: clamp(8px, 1.5vh, 20px);
          flex-wrap: wrap;
        }
        .community-link {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: #9e8b6e;
          text-decoration: none;
          font-size: 12px;
          padding: 4px 10px;
          border-radius: 4px;
          border: 1px solid transparent;
          transition: all 0.2s ease;
        }
        .community-link:hover {
          color: #ffd35a;
          border-color: rgba(255, 211, 90, 0.3);
          background: rgba(255, 211, 90, 0.08);
        }
        .community-link svg {
          width: 16px;
          height: 16px;
          flex-shrink: 0;
        }
        @media (max-height: 800px) {
          .menu-community {
            gap: 10px;
            margin-top: 6px;
          }
          .community-link {
            font-size: 11px;
            padding: 3px 8px;
          }
          .community-link svg {
            width: 14px;
            height: 14px;
          }
        }

        /* QR Code Modal */
        .qr-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          z-index: 400;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .qr-modal {
          background: linear-gradient(145deg, rgba(74,55,40,0.95) 0%, rgba(45,31,21,0.98) 100%);
          border: 2px solid #8b6914;
          border-radius: 8px;
          padding: 24px;
          text-align: center;
          box-shadow: 0 10px 40px rgba(0,0,0,0.6);
        }
        .qr-modal-title {
          font-size: 16px;
          font-weight: 700;
          color: #ffd35a;
          margin-bottom: 16px;
        }
        .qr-modal img {
          width: 200px;
          height: 200px;
          border-radius: 4px;
          background: #fff;
        }
        .qr-modal-hint {
          margin-top: 12px;
          font-size: 12px;
          color: #9e8b6e;
        }
      </style>

      <div class="particles">
        <div class="particle"></div>
        <div class="particle"></div>
        <div class="particle"></div>
        <div class="particle"></div>
        <div class="particle"></div>
        <div class="particle"></div>
        <div class="particle"></div>
        <div class="particle"></div>
        <div class="particle"></div>
        <div class="particle"></div>
        <div class="particle"></div>
        <div class="particle"></div>
        <div class="particle"></div>
        <div class="particle"></div>
        <div class="particle"></div>
      </div>

      <div class="menu-title">天下归一</div>
      <div class="menu-subtitle">七国争霸，一统天下</div>

      <div class="menu-section">
        <div class="faction-grid" id="faction-grid"></div>
      </div>

      <div class="section-divider">
        <div class="divider-line"></div>
        <div class="divider-text">选择难度</div>
        <div class="divider-line"></div>
      </div>

      <div class="menu-section">
        <div class="difficulty-row" id="difficulty-row"></div>
      </div>

      <div class="start-section">
        <button class="start-btn" id="start-btn">开始游戏</button>
      </div>

      <div class="menu-community">
        ${COMMUNITY_LINKS.map((link) =>
          link.qrImage
            ? `<a class="community-link" href="javascript:void(0)" data-qr="${link.qrImage}" data-qr-title="${link.label}" title="${link.label}">${link.icon}<span>${link.label}</span></a>`
            : `<a class="community-link" href="${link.url}" target="_blank" rel="noopener noreferrer" title="${link.label}">${link.icon}<span>${link.label}</span></a>`
        ).join('')}
      </div>

      <div class="menu-footer">
        按 <kbd>O</kbd> 键打开设置
      </div>
    `;

    document.body.appendChild(this.container);
    this.renderFactions();
    this.renderDifficulties();
    this.setupEvents();
  }

  setCallbacks(callbacks: MainMenuCallbacks): void {
    this.callbacks = callbacks;
  }

  private renderFactions(): void {
    const grid = this.container.querySelector('#faction-grid');
    if (!grid) return;

    const playableFactions: FactionId[] = ['yan', 'han', 'lin', 'shang', 'xuan', 'tie', 'yun'];

    grid.innerHTML = playableFactions
      .map((factionId) => {
        const faction = FACTIONS[factionId];
        const borderColor = FACTION_BORDER_COLORS[factionId];
        const isSelected = factionId === this.selectedFaction;
        const glowRgb = this.hexToRgb(borderColor);

        return `
          <div class="faction-card ${isSelected ? 'selected' : ''}"
               data-faction="${factionId}"
               style="--faction-bg: url('/images/factions/${factionId}.webp');
                      --faction-emblem: url('/images/emblems/${factionId}.webp');
                      --faction-border: ${borderColor};
                      --faction-glow: ${glowRgb}">
            <div class="faction-content">
              <div class="faction-header">
                <div class="faction-icon"></div>
                <span class="faction-name">${faction.name}</span>
              </div>
              <div class="faction-subtitle">${FACTION_SUBTITLES[factionId]}</div>
              <div class="faction-desc">${FACTION_DESCRIPTIONS[factionId]}</div>
              <div class="faction-bonus">
                ${FACTION_BONUSES[factionId]}
                <span class="faction-bonus-label">${FACTION_BONUS_LABELS[factionId]}</span>
              </div>
            </div>
          </div>
        `;
      })
      .join('');
  }

  private renderDifficulties(): void {
    const row = this.container.querySelector('#difficulty-row');
    if (!row) return;

    const difficulties: DifficultyLevel[] = ['normal', 'hard', 'expert'];

    row.innerHTML = difficulties
      .map((level) => {
        const isSelected = level === this.selectedDifficulty;

        return `
          <button class="difficulty-btn ${isSelected ? 'selected' : ''}" data-difficulty="${level}">
            <div class="difficulty-icon" style="background-image: url('/images/difficulty/${level}.webp')"></div>
            <div class="difficulty-info">
              <div class="difficulty-name">${getDifficultyName(level)}</div>
              <div class="difficulty-desc">${getDifficultyDescription(level)}</div>
            </div>
          </button>
        `;
      })
      .join('');
  }

  private hexToRgb(hex: string): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
      return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
    }
    return '255, 255, 255';
  }

  private setupEvents(): void {
    // Faction selection
    this.container.addEventListener('click', (e) => {
      const factionCard = (e.target as HTMLElement).closest('.faction-card') as HTMLElement;
      if (factionCard) {
        const factionId = factionCard.dataset.faction as FactionId;
        if (factionId) {
          this.selectedFaction = factionId;
          this.renderFactions();
        }
      }

      const difficultyBtn = (e.target as HTMLElement).closest('.difficulty-btn') as HTMLElement;
      if (difficultyBtn) {
        const difficulty = difficultyBtn.dataset.difficulty as DifficultyLevel;
        if (difficulty) {
          this.selectedDifficulty = difficulty;
          this.renderDifficulties();
        }
      }
    });

    // Community QR code links
    this.container.querySelectorAll('.community-link[data-qr]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        const target = el as HTMLElement;
        const qrImage = target.dataset.qr!;
        const title = target.dataset.qrTitle || '';
        this.showQrModal(title, qrImage);
      });
    });

    // Start button
    const startBtn = this.container.querySelector('#start-btn');
    startBtn?.addEventListener('click', () => {
      this.startGame();
    });

    // Enter key to start
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !this.container.classList.contains('hidden')) {
        this.startGame();
      }
    });
  }

  private showQrModal(title: string, qrImage: string): void {
    const overlay = document.createElement('div');
    overlay.className = 'qr-overlay';
    overlay.innerHTML = `
      <div class="qr-modal">
        <div class="qr-modal-title">扫码加入${title}</div>
        <img src="${qrImage}" alt="${title} QR Code">
        <div class="qr-modal-hint">点击任意处关闭</div>
      </div>
    `;
    overlay.addEventListener('click', () => overlay.remove());
    this.container.appendChild(overlay);
  }

  private startGame(): void {
    if (this.callbacks?.onStartGame) {
      this.callbacks.onStartGame(this.selectedFaction, this.selectedDifficulty);
    }
    this.hide();
  }

  show(): void {
    this.container.classList.remove('hidden');
  }

  hide(): void {
    this.container.classList.add('hidden');
  }

  dispose(): void {
    this.container.remove();
  }
}
